<script>
  import { onMount, onDestroy } from "svelte";
  import {
    computeSpanDepths,
    computeTimeRange,
    computeProcessRows,
    computeVisibleRows,
    filterCollapsedChildren,
    groupSpansByTrace,
    sortSpansForWaterfall,
    formatDur,
    buildTicks,
  } from "./TraceExplorer/utils.js";

  import Tooltip from "./TraceExplorer/Tooltip.svelte";
  import TitleBar from "./TraceExplorer/TitleBar.svelte";
  import Toolbar from "./TraceExplorer/Toolbar.svelte";
  import TimelineView from "./TraceExplorer/TimelineView.svelte";
  import WaterfallView from "./TraceExplorer/WaterfallView.svelte";
  import DetailPanel from "./TraceExplorer/DetailPanel.svelte";

  export let serial_number = "";
  export let tracing_enabled;
  export let sidecar_info = {};
  export let live;

  // Core state
  let spans = [];
  let viewMode = "timeline";
  let selectedSpan = null;
  let selectedTrace = null;

  // Tooltip state
  let tooltip = null;

  // Color encoding
  let colorBy = "name"; // "name" | "trace"

  // Inline clear confirmation
  let confirmClear = false;

  // Stats (updated via stats_update push events)
  let memoryBytes = 0;
  let maxSpans = 50000;
  let dropStrategy = "drop_oldest";

  // Resource info popover
  let showResourceInfo = false;
  let resourceAttrs = {};
  $: hasResource = Object.keys(resourceAttrs).length > 0;

  // Timeline state
  let vpStart = 0.0;
  let vpEnd = 1.0;
  let rowSort = "tree";
  let rowSearch = "";
  let spanSearch = "";
  let expandedPids = new Set();
  let hiddenPids = new Set();
  let collapsedTreePids = new Set();
  let canvasEl = null;
  let isPanning = false;
  let panStartX = 0;
  let panStartY = 0;
  let panStartScrollTop = 0;
  let panStartVP = { start: 0, end: 1 };

  const LABEL_W = 240;
  const LANE_H = 22;
  const LANE_GAP = 3;
  const ROW_PAD = 5;
  const MAX_LANES = 6;
  const WATERFALL_LABEL_W = 300;
  const MAX_SPANS = 50_000;

  // Resizable detail panel
  let detailHeight = 200;
  let isResizing = false;
  let resizeStartY = 0;
  let resizeStartH = 200;

  onMount(() => {
    live.handleEvent("new_spans", handleNewSpans);
    live.handleEvent("clear_traces", handleClearTraces);
    live.handleEvent("stats_update", handleStatsUpdate);
  });

  onDestroy(() => {
    live.removeHandleEvent(handleNewSpans);
    live.removeHandleEvent(handleClearTraces);
    live.removeHandleEvent(handleStatsUpdate);
  });

  function handleNewSpans({ spans: incoming } = {}) {
    if (!incoming) return;
    const combined = [...spans, ...incoming];
    // Enforce client-side cap matching server's 50k limit (FIFO eviction)
    spans = combined.length > MAX_SPANS ? combined.slice(combined.length - MAX_SPANS) : combined;
  }

  function handleClearTraces() {
    spans = [];
    selectedSpan = null;
    selectedTrace = null;
    vpStart = 0;
    vpEnd = 1;
    confirmClear = false;
    memoryBytes = 0;
  }

  function handleStatsUpdate({ memory_bytes, max_spans }) {
    memoryBytes = memory_bytes ?? 0;
    if (max_spans) maxSpans = max_spans;
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Derived
  $: timeRange = computeTimeRange(spans);
  $: allRows = computeProcessRows(spans);
  $: traces = groupSpansByTrace(spans);
  $: visibleRows = (() => {
    const rows = computeVisibleRows(allRows, rowSort, rowSearch, hiddenPids);
    return rowSort === "tree" ? filterCollapsedChildren(rows, collapsedTreePids) : rows;
  })();
  $: vpStartNs = timeRange.minNs + vpStart * timeRange.durationNs;
  $: vpDurNs = Math.max(1, (vpEnd - vpStart) * timeRange.durationNs);
  $: ticks = buildTicks(vpStartNs, vpDurNs, timeRange.minNs);
  $: rangeLabel = formatDur(vpDurNs);
  $: waterfallTimeRange = selectedTrace
    ? computeTimeRange(selectedTrace.spans)
    : { minNs: 0, maxNs: 0, durationNs: 1 };
  $: waterfallTicks = selectedTrace ? buildTicks(0, waterfallTimeRange.durationNs, 0, 6) : [];
  $: sortedWaterfallSpans = selectedTrace ? sortSpansForWaterfall(selectedTrace.spans) : [];
  $: spanDepths = selectedTrace ? computeSpanDepths(selectedTrace.spans) : new Map();

  // Zoom & pan
  function fitAll() {
    vpStart = 0;
    vpEnd = 1;
  }

  function applyZoom(factor, centerF = 0.5) {
    const range = vpEnd - vpStart;
    const newRange = Math.max(1e-7, Math.min(1, range * factor));
    const center = vpStart + centerF * range;
    vpStart = Math.max(0, center - centerF * newRange);
    vpEnd = vpStart + newRange;
    if (vpEnd > 1) {
      vpEnd = 1;
      vpStart = Math.max(0, 1 - newRange);
    }
  }

  function handleWheel(e) {
    if (!canvasEl) return;
    // Ignore horizontal-dominant scroll gestures (e.g. trackpad swipes)
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    // Ignore negligible deltaY to avoid spurious zoom from trackpad inertia
    if (Math.abs(e.deltaY) < 1) return;
    const rect = canvasEl.getBoundingClientRect();
    const mouseX = e.clientX - rect.left - LABEL_W;
    if (mouseX >= 0) {
      e.preventDefault();
      const canvasWidth = rect.width - LABEL_W;
      const f = Math.max(0, Math.min(1, mouseX / canvasWidth));
      applyZoom(e.deltaY > 0 ? 1.35 : 0.74, f);
    }
  }

  function startPan(e) {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    if (e.clientX - rect.left < LABEL_W) return;
    isPanning = true;
    panStartX = e.clientX;
    panStartY = e.clientY;
    panStartScrollTop = canvasEl.scrollTop;
    panStartVP = { start: vpStart, end: vpEnd };
    e.preventDefault();
  }

  function doPan(e) {
    if (!isPanning || !canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const canvasWidth = rect.width - LABEL_W;
    const dx = e.clientX - panStartX;
    const dy = e.clientY - panStartY;
    const panF = -(dx / canvasWidth) * (panStartVP.end - panStartVP.start);
    const range = panStartVP.end - panStartVP.start;
    vpStart = Math.max(0, Math.min(1 - range, panStartVP.start + panF));
    vpEnd = vpStart + range;
    canvasEl.scrollTop = panStartScrollTop - dy;
  }

  function endPan() {
    isPanning = false;
  }

  // Detail panel resize
  function startResize(e) {
    isResizing = true;
    resizeStartY = e.clientY;
    resizeStartH = detailHeight;
    e.preventDefault();
  }

  function doResize(e) {
    if (!isResizing) return;
    const dy = resizeStartY - e.clientY;
    detailHeight = Math.max(80, Math.min(600, resizeStartH + dy));
  }

  function endResize() {
    isResizing = false;
  }

  function toggleExpand(pid) {
    expandedPids.has(pid) ? expandedPids.delete(pid) : expandedPids.add(pid);
    expandedPids = expandedPids;
  }

  function toggleHide(pid) {
    hiddenPids.has(pid) ? hiddenPids.delete(pid) : hiddenPids.add(pid);
    hiddenPids = hiddenPids;
  }

  function toggleTreeCollapse(pid) {
    collapsedTreePids.has(pid) ? collapsedTreePids.delete(pid) : collapsedTreePids.add(pid);
    collapsedTreePids = collapsedTreePids;
  }

  function selectSpan(span) {
    selectedSpan = span;
  }

  function selectTrace(trace) {
    selectedTrace = trace;
    viewMode = "waterfall";
  }

  function openTraceFromSpan(span) {
    const trace = traces.find((t) => t.trace_id === span.trace_id);
    if (trace) selectTrace(trace);
  }

  // Double-click on canvas background: zoom in centered on click position
  function handleCanvasDblClick(e) {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const clickX = e.clientX - rect.left - LABEL_W;
    if (clickX < 0) return;
    const canvasWidth = rect.width - LABEL_W;
    const centerF = Math.max(0, Math.min(1, clickX / canvasWidth));
    applyZoom(0.4, centerF);
  }

  // Double-click on a span: zoom to fit that span with padding
  function zoomToSpan(span) {
    if (!timeRange.durationNs) return;
    const padding = 0.2;
    const spanStart = (span.start_time_ns - timeRange.minNs) / timeRange.durationNs;
    const spanEnd = (span.end_time_ns - timeRange.minNs) / timeRange.durationNs;
    const spanDur = spanEnd - spanStart;
    const pad = Math.max(spanDur * padding, 1e-6);
    vpStart = Math.max(0, spanStart - pad);
    vpEnd = Math.min(1, spanEnd + pad);
  }

  // Navigate to the first span in a row (label click)
  function navigateToFirstSpan(row) {
    if (!row.spans.length || !timeRange.durationNs) return;
    const firstSpan = row.spans.reduce((min, s) => (s.start_time_ns < min.start_time_ns ? s : min));
    selectSpan(firstSpan);
    const spanDurNs = Math.max(firstSpan.end_time_ns - firstSpan.start_time_ns, 1e6);
    const padding = spanDurNs * 1.5;
    const viewStartNs = firstSpan.start_time_ns - padding;
    const viewEndNs = firstSpan.end_time_ns + padding;
    vpStart = Math.max(0, (viewStartNs - timeRange.minNs) / timeRange.durationNs);
    vpEnd = Math.min(1, (viewEndNs - timeRange.minNs) / timeRange.durationNs);
  }

  // Tracing controls
  function handleToggleTracing() {
    live.pushEvent("toggle_tracing", { enabled: String(!tracing_enabled) });
  }

  function handleClearButton() {
    if (!confirmClear) {
      confirmClear = true;
      return;
    }
    confirmClear = false;
    live.pushEvent("clear_traces", {});
  }

  function cancelClear() {
    confirmClear = false;
  }

  // Tooltip
  function showSpanTooltip(e, span) {
    tooltip = { x: e.clientX, y: e.clientY, span };
  }
  function moveSpanTooltip(e) {
    if (tooltip) tooltip = { ...tooltip, x: e.clientX, y: e.clientY };
  }
  function hideSpanTooltip() {
    tooltip = null;
  }
</script>

<Tooltip {tooltip} />

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="trace-explorer flex flex-col bg-surface"
  on:mousemove={(e) => {
    doPan(e);
    doResize(e);
  }}
  on:mouseup={() => {
    endPan();
    endResize();
  }}
  on:mouseleave={() => {
    endPan();
    endResize();
  }}
>
  <TitleBar
    {serial_number}
    {sidecar_info}
    {spans}
    {maxSpans}
    {memoryBytes}
    {formatBytes}
    {dropStrategy}
    {tracing_enabled}
    {confirmClear}
    {showResourceInfo}
    {hasResource}
    {resourceAttrs}
    on:toggleTracing={handleToggleTracing}
    on:clearButton={handleClearButton}
    on:cancelClear={cancelClear}
    on:toggleResourceInfo={() => (showResourceInfo = !showResourceInfo)}
  />

  <Toolbar
    bind:viewMode
    {spans}
    {traces}
    {allRows}
    {rangeLabel}
    bind:colorBy
    bind:rowSort
    bind:rowSearch
    bind:spanSearch
    {hiddenPids}
    {visibleRows}
    on:fitAll={fitAll}
    on:zoomOut={() => applyZoom(2)}
    on:zoomIn={() => applyZoom(0.5)}
    on:toggleHide={(e) => toggleHide(e.detail.pid)}
  />

  <!-- Main content -->
  <div class="flex-1 min-h-0 flex flex-col">
    {#if viewMode === "timeline"}
      <TimelineView
        {visibleRows}
        {ticks}
        {vpStartNs}
        {vpDurNs}
        {selectedSpan}
        {colorBy}
        {spanSearch}
        {expandedPids}
        {collapsedTreePids}
        {rowSort}
        {tracing_enabled}
        {allRows}
        {isPanning}
        bind:canvasEl
        {LABEL_W}
        {LANE_H}
        {LANE_GAP}
        {ROW_PAD}
        {MAX_LANES}
        on:toggleTracing={handleToggleTracing}
        on:wheel={(e) => handleWheel(e.detail)}
        on:startPan={(e) => startPan(e.detail)}
        on:canvasDblClick={(e) => handleCanvasDblClick(e.detail)}
        on:navigateToFirstSpan={(e) => navigateToFirstSpan(e.detail.row)}
        on:toggleTreeCollapse={(e) => toggleTreeCollapse(e.detail.pid)}
        on:toggleExpand={(e) => toggleExpand(e.detail.pid)}
        on:toggleHide={(e) => toggleHide(e.detail.pid)}
        on:selectSpan={(e) => selectSpan(e.detail.span)}
        on:zoomToSpan={(e) => zoomToSpan(e.detail.span)}
        on:showSpanTooltip={(e) => showSpanTooltip(e.detail.event, e.detail.span)}
        on:moveSpanTooltip={(e) => moveSpanTooltip(e.detail.event)}
        on:hideSpanTooltip={hideSpanTooltip}
      />
    {:else}
      <WaterfallView
        {traces}
        {selectedTrace}
        {selectedSpan}
        {waterfallTimeRange}
        {waterfallTicks}
        {sortedWaterfallSpans}
        {spanDepths}
        {colorBy}
        {WATERFALL_LABEL_W}
        on:selectTrace={(e) => selectTrace(e.detail.trace)}
        on:selectSpan={(e) => selectSpan(e.detail.span)}
      />
    {/if}
  </div>

  <DetailPanel
    {selectedSpan}
    {detailHeight}
    {isResizing}
    {colorBy}
    on:startResize={(e) => startResize(e.detail)}
    on:openTrace={(e) => openTraceFromSpan(e.detail.span)}
    on:close={() => (selectedSpan = null)}
  />
</div>

<style>
  .trace-explorer {
    /* nav: 64px, no page padding (full_page layout) */
    height: calc(100vh - 64px);
  }
</style>
